import { createContext, useContext, useState, useMemo } from 'react';

const AppContext = createContext(null);

// Get current date info for initial defaults
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth(); // 0-indexed

export const AppProvider = ({ children }) => {
  // Default to current year instead of hardcoded 2025
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  // Default to YTD (Year-to-Date) as the primary executive reporting view
  const [periodType, setPeriodType] = useState('ytd');
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Helper function to format date - must be defined before useMemo that uses it
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate period buckets based on year and period type
  const periodBuckets = useMemo(() => {
    const buckets = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const daysInMonth = [31, selectedYear % 4 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
    if (periodType === 'weekly') {
      // Generate 48 weeks (4 per month) for the year
      let globalWeekNum = 1;
      
      for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
        const monthDays = daysInMonth[monthIdx];
        const monthName = months[monthIdx];
        const daysPerWeek = Math.floor(monthDays / 4);
        const remainder = monthDays % 4;
        
        let currentDay = 1;
        
        for (let weekInMonth = 1; weekInMonth <= 4; weekInMonth++) {
          // Add extra day to earlier weeks if there's remainder
          const weekDays = daysPerWeek + (weekInMonth <= remainder ? 1 : 0);
          
          const startDate = new Date(selectedYear, monthIdx, currentDay);
          const endDay = Math.min(currentDay + weekDays - 1, monthDays);
          const endDate = new Date(selectedYear, monthIdx, endDay);
          
          buckets.push({
            id: `${selectedYear}-W${globalWeekNum}`,
            label: `${selectedYear} • ${monthName} Week ${weekInMonth}`,
            shortLabel: `${monthName} W${weekInMonth}`,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            year: selectedYear,
            periodType: 'weekly',
            weekNum: globalWeekNum,
            monthWeekNum: weekInMonth,
            month: monthIdx + 1
          });
          
          currentDay += weekDays;
          globalWeekNum++;
        }
      }
    } else if (periodType === 'monthly') {
      // Generate 12 months
      
      months.forEach((month, idx) => {
        const monthNum = String(idx + 1).padStart(2, '0');
        buckets.push({
          id: `${selectedYear}-${monthNum}`,
          label: `${selectedYear} • ${month}`,
          shortLabel: month,
          startDate: `${selectedYear}-${monthNum}-01`,
          endDate: `${selectedYear}-${monthNum}-${daysInMonth[idx]}`,
          year: selectedYear,
          periodType: 'monthly',
          monthNum: idx + 1
        });
      });
    } else if (periodType === 'quarterly') {
      // Generate 4 quarters
      const quarters = [
        { q: 1, start: '01-01', end: '03-31', label: 'Q1' },
        { q: 2, start: '04-01', end: '06-30', label: 'Q2' },
        { q: 3, start: '07-01', end: '09-30', label: 'Q3' },
        { q: 4, start: '10-01', end: '12-31', label: 'Q4' },
      ];
      
      quarters.forEach(({ q, start, end, label }) => {
        buckets.push({
          id: `${selectedYear}-Q${q}`,
          label: `${selectedYear} • ${label}`,
          shortLabel: label,
          startDate: `${selectedYear}-${start}`,
          endDate: `${selectedYear}-${end}`,
          year: selectedYear,
          periodType: 'quarterly',
          quarter: q
        });
      });
    } else if (periodType === 'yearly') {
      // Single year bucket
      buckets.push({
        id: `${selectedYear}`,
        label: `${selectedYear}`,
        shortLabel: `${selectedYear}`,
        startDate: `${selectedYear}-01-01`,
        endDate: `${selectedYear}-12-31`,
        year: selectedYear,
        periodType: 'yearly'
      });
    } else if (periodType === 'ytd') {
      // Year-to-Date: Jan 1 to today (or Dec 31 for past years)
      const today = new Date();
      const isCurrentYear = selectedYear === today.getFullYear();
      
      let ytdEndDate;
      let ytdEndLabel;
      
      if (isCurrentYear) {
        // Current year: Jan 1 to today
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();
        ytdEndDate = formatDate(today);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        ytdEndLabel = `${months[currentMonth]} ${currentDay}`;
      } else {
        // Past year: Full year (Jan 1 - Dec 31)
        ytdEndDate = `${selectedYear}-12-31`;
        ytdEndLabel = 'Dec 31';
      }
      
      buckets.push({
        id: `${selectedYear}-YTD`,
        label: `${selectedYear} YTD (Jan 1 - ${ytdEndLabel})`,
        shortLabel: 'YTD',
        startDate: `${selectedYear}-01-01`,
        endDate: ytdEndDate,
        year: selectedYear,
        periodType: 'ytd',
        isCurrentYear: isCurrentYear
      });
    }
    
    return buckets;
  }, [selectedYear, periodType]);

  // Auto-select current/latest period when period type or year changes
  useMemo(() => {
    if (periodBuckets.length > 0) {
      const now = new Date();
      const currentYear = now.getFullYear();
      
      if (selectedYear === currentYear) {
        // Find current period
        const currentMonth = now.getMonth();
        const currentDay = now.getDate();
        
        if (periodType === 'monthly') {
          setSelectedPeriod(periodBuckets[currentMonth] || periodBuckets[periodBuckets.length - 1]);
        } else if (periodType === 'quarterly') {
          const currentQuarter = Math.floor(currentMonth / 3);
          setSelectedPeriod(periodBuckets[currentQuarter] || periodBuckets[periodBuckets.length - 1]);
        } else if (periodType === 'weekly') {
          // Find current week (4 weeks per month = 48 total)
          // Calculate which week of the current month we're in
          const daysInCurrentMonth = new Date(selectedYear, currentMonth + 1, 0).getDate();
          const daysPerWeek = Math.floor(daysInCurrentMonth / 4);
          const weekInMonth = Math.min(Math.floor((currentDay - 1) / daysPerWeek), 3); // 0-3
          const currentWeekIndex = (currentMonth * 4) + weekInMonth;
          setSelectedPeriod(periodBuckets[currentWeekIndex] || periodBuckets[periodBuckets.length - 1]);
        } else if (periodType === 'ytd' || periodType === 'yearly') {
          // YTD and Yearly have single bucket
          setSelectedPeriod(periodBuckets[0]);
        } else {
          setSelectedPeriod(periodBuckets[0]);
        }
      } else {
        // For past/future years, select the last period
        setSelectedPeriod(periodBuckets[periodBuckets.length - 1]);
      }
    }
  }, [periodType, selectedYear, periodBuckets.length]);

  // Dynamic years array - always include current year and a few years before/after
  const years = useMemo(() => {
    const yearList = [];
    for (let y = CURRENT_YEAR - 3; y <= CURRENT_YEAR + 1; y++) {
      yearList.push(y);
    }
    return yearList;
  }, []);
  
  const periodTypes = [
    { value: 'ytd', label: 'YTD' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  return (
    <AppContext.Provider value={{
      selectedYear,
      setSelectedYear,
      periodType,
      setPeriodType,
      selectedPeriod,
      setSelectedPeriod,
      periodBuckets,
      years,
      periodTypes
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
